import { useTenantNavigate } from '@app-store'
import { useLocation } from 'react-router-dom'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import {
  ConfirmDialog,
  CustomAgTable,
  CustomButton,
  Modal,
  Pagination,
  SORT_KEYS,
  SvgWarning,
  TableActionType,
  TableColumnType,
  tableFilterType,
} from '@ui-kit'
import {
  DataType,
  DataTypeStore,
  deleteQueueByIdApi,
  hideLoader,
  pageableQueuesApi,
  showLoader,
  TableFilterObjectBody,
  toggleManageQueueAPI,
  deleteQueue as deleteQueueReducer,
  updateAuthUserSelectedTenant,
  toggleQueueReducer,
  queueListingType,
  mapQueueCreationNav,
  getStringFilterOptions,
  SearchCriteria,
  searchURLManageQueue,
  updateNumOfRows,
  PayloadQueueConfig,
  addToaster,
  setClientDropdown,
  getInboundMailQueueStateApi,
  getFaxIntakeQueueStateApi,
} from '@app-store'
import {
  FIELD_VALUE_CONSTANTS,
  NAMESPACES,
  PAGINATION,
  PERMISSION_MODULES,
} from '../../utils/constants'
import { isEmpty, uniqueId } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import {
  convertToCamelCase,
  programPermissionsObject,
} from '../../utils/helperFunctions'
import { formatFilterArrayFields } from '../../utils/listing.utils'
import { isSuperAdmin } from '../../utils/permission'
import NegativeScreen from '../../components/queue-config/NegativeScreen'
import {
  MANAGE_QUEUES_MESSAGES,
  STRINGS,
  USER_PROFILE_MANAGEMENT,
} from '../../utils/strings'
import { useFocusFirstElement } from '../../hooks/useFocusFirstFocusableElement'
import { formatLocalDateWithZone } from '../../utils/date.utils'

const ManageQueue = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useTenantNavigate()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const selectedId = useAppSelector(
    (state) => state?.authUser?.selectedClientTenant?.id
  )
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions.programPermissions
  )

  const numOfPage = useAppSelector((state) => state?.pagination.numOfRows)

  const getStringOptions = async (searchCriteria: SearchCriteria) => {
    return getStringFilterOptions(searchURLManageQueue, searchCriteria)
  }

  const userProfile = useAppSelector(
    (state: RootState) => state?.authUser?.user?.profile
  )
  useFocusFirstElement(containerRef)
  const columns: TableColumnType<DataTypeStore>[] = [
    {
      header: NAMESPACES.NAME_LABEL,
      accessor: NAMESPACES.NAME_KEY,
      isMandatory: true,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: tableFilterType.STRING,
        onKeyPress: getStringOptions,
      },
    },
    {
      header: NAMESPACES.STATUS_LABEL,
      accessor: NAMESPACES.STATUS_KEY,
      isMandatory: true,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: tableFilterType.STRING,
        onKeyPress: getStringOptions,
      },
    },
    {
      header: NAMESPACES.ASSIGNED_GROUP_LABEL,
      accessor: NAMESPACES.ASSIGNED_GROUP_KEY,
      isMandatory: true,
      minWidth: '3',
      isSortable: true,
      filterData: {
        dataType: tableFilterType.STRING,
      },
    },
    {
      header: NAMESPACES.CREATED_ON_LABEL,
      accessor: NAMESPACES.CREATED_ON_KEY,
      type: 'string',
      isMandatory: true,
      isSortable: true,
      filterData: {
        dataType: tableFilterType.DATE,
      },
    },
    {
      header: NAMESPACES.UPDATED_ON_LABEL,
      accessor: NAMESPACES.UPDATED_ON_KEY,
      isSortable: true,
      filterData: {
        dataType: tableFilterType.DATE,
      },
    },
  ]

  const actions: TableActionType<DataTypeStore>[] = [
    {
      label: NAMESPACES.VIEW,
      key: NAMESPACES.VIEW_KEY,
      showInPopUp: false,
      isEnabledOnDisabledRow: true,
    },
    {
      label: NAMESPACES.EDIT,
      key: NAMESPACES.EDIT_KEY,
      showInPopUp: false,
    },
    {
      label: NAMESPACES.TOGGLE,
      key: NAMESPACES.TOGGLE,
      accessor: NAMESPACES.ENABLED,
      showInPopUp: false,
      type: NAMESPACES.TOGGLE,
      isEnabledOnDisabledRow: true,
    },
    {
      label: NAMESPACES.DELETE,
      key: NAMESPACES.DELETE_KEY,
      showInPopUp: true,
    },
  ]

  const [clientChange, setClientChange] = useState<number>(-10)
  const [data, setData] = useState<DataTypeStore[]>([])
  const [rowPerPage, setRowPerPage] = useState<number>(PAGINATION.ROWS_PER_PAGE)
  const [currentPage, setCurrentPage] = useState<number>(
    FIELD_VALUE_CONSTANTS.ONE
  )
  const [totalPages, setTotalPages] = useState<number>(
    FIELD_VALUE_CONSTANTS.ONE
  )
  const [apiFilter, setApiFilter] = useState<TableFilterObjectBody>({
    fields: null,
    search_criteria: {},
  })
  // const [userList, setUserList] = useState<DropdownType<AuthUserClient>[]>([])
  const selectedClientProgram = useAppSelector(
    (state: RootState) => state?.clientPrograms?.selectedClientPrograms
  )
  const tenantId = selectedClientProgram?.tenant_id
  const programId = selectedClientProgram?.program_id

  const [modal, setModal] = useState<{
    queueData?: DataType
    state: boolean
    id: number
    entityId?: number
    layoutId?: number
    enabled?: string | boolean | number | string[] | number[]
    message?: string
  }>({
    queueData: {},
    state: false,
    id: -1,
    entityId: -1,
    layoutId: -1,
    enabled: '',
  })
  const [sortDirection, setSortDirection] = useState<{
    keyName: string
    direction: SORT_KEYS
    isAsc: SORT_KEYS
    key: string
  }>({
    key: NAMESPACES.UPDATED_ON_KEY,
    isAsc: SORT_KEYS.Desc,
    keyName: 'modifiedAt',
    direction: SORT_KEYS.Desc,
  })

  const handleClientChange = (value: number | undefined) => {
    if (value) {
      setClientChange(value)
      setCurrentPage(FIELD_VALUE_CONSTANTS.ONE)
      dispatch(updateAuthUserSelectedTenant(value))
    }
  }

  const openToggleModal = (row: DataType, message?: string) => {
    setModal({
      queueData: row,
      state: true,
      id: Number(row?.id),
      enabled: row?.enabled,
      message,
    })
  }

  const handleQueueToggleClick = async (row: DataType) => {
    if (!row?.id) return

    try {
      dispatch(showLoader())
      const [mailResponse, faxResponse] = await Promise.all([
        getInboundMailQueueStateApi(Number(row.id)),
        getFaxIntakeQueueStateApi(Number(row.id)),
      ])
      const mailQueueState = mailResponse?.data
      const faxQueueState = faxResponse?.data
      const isEnabling = row?.enabled !== true

      if (mailQueueState?.mailQueue) {
        if (isEnabling && !mailQueueState.inboundMailEnabled) {
          openToggleModal(
            row,
            MANAGE_QUEUES_MESSAGES.ENABLE_MAIL_QUEUE_INBOUND_DISABLED
          )
          return
        }

        if (!isEnabling && mailQueueState.inboundMailEnabled) {
          openToggleModal(
            row,
            MANAGE_QUEUES_MESSAGES.DISABLE_MAIL_QUEUE_INBOUND_ENABLED
          )
          return
        }

        openToggleModal(row)
        return
      }

      if (faxQueueState?.faxQueue) {
        if (isEnabling && !faxQueueState.inboundFaxEnabled) {
          openToggleModal(
            row,
            MANAGE_QUEUES_MESSAGES.ENABLE_FAX_QUEUE_INBOUND_DISABLED
          )
          return
        }

        if (!isEnabling && faxQueueState.inboundFaxEnabled) {
          openToggleModal(
            row,
            MANAGE_QUEUES_MESSAGES.DISABLE_FAX_QUEUE_INBOUND_ENABLED
          )
          return
        }

        openToggleModal(row)
        return
      }

      openToggleModal(row)
    } catch (error) {
      console.error('Failed to fetch intake queue state', error)
      openToggleModal(row)
    } finally {
      dispatch(hideLoader())
    }
  }

  const onActionClick = (
    actionKeyName: string,
    row: DataType,
    _rowIndex: number
  ) => {
    switch (actionKeyName) {
      case NAMESPACES.EDIT_KEY:
        navigate(`${selectedId}/edit-queue/${row?.id}`, {
          state: { actionMode: 'edit', edit_entity_id: row?.entity_id },
        })
        break
      case NAMESPACES.VIEW_KEY:
        navigate(`${selectedId}/view-queue/${row?.id}`, {
          state: { actionMode: 'view', edit_entity_id: row?.entity_id },
        })
        break
      case NAMESPACES.TOGGLE:
        handleQueueToggleClick(row)
        break
      case NAMESPACES.DELETE_KEY:
        setModal({
          id: Number(row?.id),
          entityId: Number(row?.entity_id),
          layoutId: Number(row?.layout_id),
          state: true,
          enabled: '',
        })
        break
      default:
        break
    }
  }

  const onColumnClick = (
    isHeader: boolean,
    KeyName: keyof DataType,
    row?: DataType | null
  ) => {
    if (!isHeader) {
      if (KeyName) {
        navigate(`entity/${row?.id}/overview`)
      }
    }
  }

  const onFilterChange = (value: TableFilterObjectBody) => {
    setApiFilter(value)
  }

  const onSortClick = (key: string, isAsc: SORT_KEYS) => {
    if (key) {
      let keyNameValue: string
      if (isAsc !== SORT_KEYS.Disabled) {
        const camelKey = convertToCamelCase(key)
        keyNameValue =
          camelKey === NAMESPACES.ASSIGNED_GROUP_PASCAL
            ? NAMESPACES.ASSIGNED_GROUP_SORT
            : camelKey
      }
      setSortDirection(() => ({
        keyName: keyNameValue,
        direction: isAsc === SORT_KEYS.Asc ? SORT_KEYS.Asc : SORT_KEYS.Desc,
        key: isAsc === SORT_KEYS.Disabled ? '' : key,
        isAsc: isAsc,
      }))
    }
  }

  const fetchQueueData = async (
    page: number | undefined,
    size: number | undefined
  ) => {
    try {
      dispatch(showLoader())
      const programIds = getProgramIds()
      const programIdsFilter = {
        search_key: 'QUEUE_PROGRAMS',
        operator: 'IN',
        search_value: programIds,
      }

      const userQueueType = {
        search_key: 'QUEUE_TYPE',
        operator: 'EQUALS',
        search_value: ['USER'],
      }

      const filter = formatFilterArrayFields(apiFilter)
      filter.search_criteria.push(userQueueType)
      if (!isSuperAdmin()) {
        filter.search_criteria.push(programIdsFilter)
      }
      const response = await pageableQueuesApi({
        page: rowPerPage !== size ? 0 : (page ?? 1) - 1,
        size: size ?? rowPerPage,
        sort: !isEmpty(sortDirection?.key)
          ? `${sortDirection?.keyName},${sortDirection?.direction}`
          : undefined,
        filter: filter,
      })

      if (response) {
        setCurrentPage(rowPerPage !== size ? 1 : page ?? 1)
        setRowPerPage(size ?? 25)
        const transformedData = response?.data?.content.map(
          (item: DataTypeStore) => {
            const groupConfig: { name: string } =
              item?.queue_group_id as unknown as { name: string }
            const groupName = groupConfig?.name
            return {
              ...item,
              disabled: !item.enabled,
              group_name: groupName,
              status: item.enabled ? 'Active' : 'Inactive',
              modified_at:
                formatLocalDateWithZone(item?.modified_at as string) ?? '--',
              created_at:
                formatLocalDateWithZone(item?.created_at as string) ?? '--',
            }
          }
        )

        const PopulateTableData = async () => {
          const cloneQueues = structuredClone(transformedData)
          const enabledData: DataTypeStore[] = []
          const disabledData: DataTypeStore[] = []
          for (const queue of cloneQueues) {
            if (queue?.enabled) {
              queue.disabled = !queue?.enabled
              enabledData.push(queue)
            } else {
              queue.disabled = !queue?.enabled
              disabledData.push(queue)
            }
          }
          const allData = [...enabledData, ...disabledData]
          const filteredData = allData.filter(
            (item) =>
              item.name !== NAMESPACES?.MY_CASES &&
              item.name !== NAMESPACES?.MY_TASKS
          )
          setData(filteredData)
        }

        PopulateTableData()
        setTotalPages(response?.data?.totalPages)
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleUserPageChange = async (page: number, rowsPerPage: number) => {
    try {
      await fetchQueueData(page, rowsPerPage)
    } catch (err) {
      console.error('Error:', err)
    } finally {
      setCurrentPage(rowPerPage !== rowsPerPage ? 1 : page)
      setRowPerPage(rowsPerPage)
    }
  }

  const deleteQueue = async (id: number) => {
    setModal({ id: -1, state: false, enabled: '' })
    const response = await deleteQueueByIdApi(id)
    if (response) {
      dispatch(
        addToaster({
          id: uniqueId(),
          message: MANAGE_QUEUES_MESSAGES.DELETE_TOASTER_MSG,
          type: 'success',
        })
      )
      dispatch(deleteQueueReducer(id))
      fetchQueueData(currentPage, rowPerPage)
    }
  }

  const fetchAllQueues = async () => {
    try {
      const programIds = getProgramIds()
      const programIdsFilter = {
        search_key: 'QUEUE_PROGRAMS',
        operator: 'IN',
        search_value: programIds,
      }

      const payload = {
        filter: {
          fields: null,
          search_criteria: isSuperAdmin() ? [] : [programIdsFilter],
        },
      }

      const queueResponse = await pageableQueuesApi(payload)

      let mergedQueues = queueResponse?.data?.content ?? []

      if (!isSuperAdmin()) {
        const payloadWithoutFilter = {
          filter: {
            fields: null,
            search_criteria: [],
          },
        }

        const queueResponseWithoutFilter = await pageableQueuesApi(
          payloadWithoutFilter
        )

        const filteredProgramUnassignedQueues =
          queueResponseWithoutFilter?.data?.content?.filter(
            (queue: PayloadQueueConfig) =>
              !queue?.queue_programs || queue.queue_programs?.length === 0
          ) ?? []

        mergedQueues = [...mergedQueues, ...filteredProgramUnassignedQueues]
      }

      dispatch(mapQueueCreationNav(mergedQueues))
    } catch (err) {
      console.error('error : ', err)
    }
  }

  const toggleQueue = async (
    queueData: DataType,
    id: number,
    enabled: boolean
  ) => {
    setModal({ id: -1, state: false, enabled: '', message: undefined })
    const response = await toggleManageQueueAPI(id, enabled)
    const payload: queueListingType = {
      id: Number(queueData?.id),
      name: queueData?.name?.toString(),
      description: queueData?.description?.toString(),
      entity_id: Number(queueData?.entity_id),
      entity_name: queueData?.entity_name?.toString(),
      layout_id: Number(queueData?.layout_id),
      status: queueData?.status.toString(),
      enabled: Boolean(queueData?.enabled),
      deleted: Boolean(queueData?.deleted),
      step: Number(queueData?.step),
    }
    if (response) {
      fetchQueueData(currentPage, rowPerPage)
      fetchAllQueues()
      dispatch(toggleQueueReducer({ queueData: payload, enabled, id }))
    }
  }

  const getMessage = () => {
    if (modal?.message) {
      return modal.message
    }
    if (modal?.enabled === '') {
      return NAMESPACES.DELETE_MSG
    }
    return modal?.enabled !== true
      ? NAMESPACES.ENABLE_MSG
      : NAMESPACES.DISABLE_MSG
  }

  const handleModalConfirm = () => {
    modal?.enabled === ''
      ? deleteQueue(modal.id)
      : toggleQueue(modal?.queueData ?? {}, modal?.id, Boolean(modal?.enabled))
  }

  const handleModalCancel = () => {
    setModal({
      id: -1,
      state: false,
      enabled: '',
      message: undefined,
    })
  }

  const getProgramIds = () => {
    const programIdsWithPermission =
      programPermissionsObject.Queues_And_Cases_View(
        programPermissions,
        PERMISSION_MODULES.QUEUE_AND_CASE_VISIBILITY,
        'View Only'
      )
    return programIdsWithPermission
  }

  // useEffect(() => {
  //   const options: { label: string; value: string }[] = []
  //   const enabledClients = allTenants?.filter(
  //     (item: AuthUserClient) => item.enabled
  //   )
  //   const globalEntry = enabledClients?.find(
  //     (item) => item.name_of_organization === 'Global'
  //   )
  //   const sortedClients = enabledClients
  //     ?.filter((item) => item.name_of_organization !== 'Global')
  //     .sort((a, b) =>
  //       a.name_of_organization.localeCompare(b.name_of_organization)
  //     )
  //   if (globalEntry) {
  //     options.push({
  //       label: globalEntry.name_of_organization,
  //       value: globalEntry.id?.toString() || '',
  //     })
  //   }
  //   sortedClients?.forEach((item) => {
  //     options.push({
  //       label: item.name_of_organization,
  //       value: item.id?.toString() || '',
  //     })
  //   })

  //   const selectedTenant = options?.find(
  //     (tenant) => selectedId === +tenant?.value
  //   )
  //   reset({
  //     queue: selectedTenant?.value ?? '',
  //   })
  //   setUserList(options)
  // }, [allTenants])

  useEffect(() => {
    fetchAllQueues()
  }, [])

  useEffect(() => {
    fetchQueueData(currentPage, rowPerPage)
  }, [clientChange, sortDirection, apiFilter, programPermissions])
  useEffect(() => {
    if (userProfile === USER_PROFILE_MANAGEMENT.STANDARD_USER) {
      navigate('/')
    }
  }, [])

  useEffect(() => {
    dispatch(
      setClientDropdown({ isVisible: true, onChange: handleClientChange })
    )
  }, [location])

  return (
    <div className="h-full" ref={containerRef}>
      {userProfile !== USER_PROFILE_MANAGEMENT.STANDARD_USER ? (
        <div className="h-full font-primary flex flex-col">
          <div className="flex flex-col px-3 py-2 border-b-2">
            <div className="flex grid-cols-2 justify-between items-center">
              <h2 className="text-base font-primary font-medium">
                {NAMESPACES.MANAGE_QUEUE_LABEL}
              </h2>
              <div className="flex justify-between items-center gap-4">
                {/* <SearchBar
                  dataQaId="search-bar-manage-queues"
                  onSearch={noop}
                  width="w-[20rem]"
                  height="h-10"
                  onFilter={noop}
                /> */}
                <CustomButton
                  dataQaId="custom-button-create-new-queue"
                  color="primary"
                  isFilled={true}
                  className="text-accent-0 text-xs h-10"
                  widthClass="w-[8.5rem]"
                  onClick={() => {
                    navigate(`${selectedId}/create-queue`, {
                      state: { actionMode: 'add' },
                    })
                  }}
                >
                  {NAMESPACES.CREATE_NEW_QUEUE_LABEL}
                </CustomButton>
              </div>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col h-full bg-accent-0">
            <div className="h-full flex flex-col min-h-0 text-accent-900 table-wrap">
              <CustomAgTable<DataTypeStore>
                dataQaId="custom-table-manage-queue"
                pageName={`${tenantId}-${programId}-manage-queue-listing-page`}
                tableName={`${tenantId}-${programId}-manage-queue-listing-table`}
                key={`${tenantId}-${programId}`}
                data={data || []}
                columns={columns || []}
                actions={actions || []}
                onSortClick={onSortClick}
                sort={
                  sortDirection.key && !isEmpty(sortDirection?.key)
                    ? {
                        key: sortDirection?.key,
                        isAsc: sortDirection?.isAsc,
                      }
                    : null
                }
                showTableColumnOnEmpty
                onColumnClick={onColumnClick}
                onActionClick={onActionClick}
                disabledKey={'disabled'}
                emptyDataMessage={'No Records Found'}
                onFilterChange={onFilterChange}
                filterObject={apiFilter}
              />
              {data.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handleUserPageChange}
                  onRowsChange={(rowsPerPage) => {
                    dispatch(
                      updateNumOfRows({
                        numOfRows: rowsPerPage,
                        key: 'manage-queue-listing-table',
                      })
                    )
                  }}
                  rowsPer={
                    numOfPage?.['manage-queue-listing-table']
                      ? numOfPage['manage-queue-listing-table']
                      : PAGINATION.ROWS_PER_PAGE
                  }
                />
              )}
            </div>
          </div>
          <ConfirmDialog
            showCloseButtton={false}
            open={modal.state && !modal.message}
            confirmButtonText={NAMESPACES.YES}
            cancelButtonText={NAMESPACES.NO}
            message={getMessage()}
            onConfirm={handleModalConfirm}
            onCancel={handleModalCancel}
          />
          <Modal
            isOpen={modal.state && !!modal.message}
            closeModal={handleModalCancel}
            showCloseButton={false}
            width="w-[42rem]"
          >
            <>
              <div className="px-4 py-8 flex items-center mb-4 justify-center text-sm border-b">
                <div className="flex items-start gap-4 w-full">
                  <div className="bg-error-100 rounded-full h-[3.625rem] min-w-[3.625rem] flex items-center justify-center self-start">
                    <SvgWarning />
                  </div>
                  <span className="text-sm font-medium text-accent-700 whitespace-pre-wrap break-words min-w-0 flex-1">
                    {modal.message}
                  </span>
                </div>
              </div>
              <div className="flex gap-3 items-center justify-end w-full">
                <CustomButton
                  onClick={handleModalCancel}
                  color="primary"
                  className="border border-primary-500 text-xs"
                  height="h-8"
                >
                  {NAMESPACES.NO}
                </CustomButton>
                <CustomButton
                  onClick={handleModalConfirm}
                  color="primary"
                  isFilled={true}
                  height="h-8"
                >
                  {NAMESPACES.YES}
                </CustomButton>
              </div>
            </>
          </Modal>
        </div>
      ) : (
        <NegativeScreen message={STRINGS.QUEUE_NEGATIVE_SCREEN_VERBIAGE} />
      )}
    </div>
  )
}

export default ManageQueue
